import React from "react";

// External
import styled, { css } from "styled-components";
import PropTypes from "prop-types";

// Internal
import { components, fontFamily, interaction } from "../../../../theme";

const getVariantKey = (variant) =>
  interaction.button[variant] ? variant : "solid";

const BUTTON_STYLE_PROPS = new Set([
  "variant",
  "size",
  "bgColor",
  "textColor",
  "hoverBgColor",
  "hoverTextColor",
  "hoverBorderColor",
  "fullWidth",
]);

const ButtonStyles = styled.button.withConfig({
  shouldForwardProp: (prop) => !BUTTON_STYLE_PROPS.has(prop),
})`
  cursor: pointer;
  user-select: none;
  -webkit-user-drag: none;
  transition: ${interaction.transition};
  text-decoration: none;

  border: 1px solid
    ${(props) => {
      const palette = interaction.button[getVariantKey(props.variant)];
      if (props.disabled) {
        return palette.disabled.border;
      }
      if (props.variant === "ghost") {
        return "transparent";
      }
      return props.bgColor || palette.default.border;
    }};

  background-color: ${(props) => {
    const palette = interaction.button[getVariantKey(props.variant)];
    if (props.disabled) {
      return palette.disabled.background;
    }
    if (props.variant === "solid") {
      return props.bgColor || palette.default.background;
    }
    return palette.default.background;
  }};

  color: ${(props) => {
    const palette = interaction.button[getVariantKey(props.variant)];
    if (props.disabled) {
      return palette.disabled.color;
    }
    if (props.variant === "outlined" || props.variant === "ghost") {
      return props.bgColor || palette.default.color;
    }
    return props.textColor || palette.default.color;
  }};

  font-size: ${(props) => components.button.fontSize[props.size]};
  font-family: ${fontFamily.bold};

  padding: ${(props) => components.button.padding[props.size]};
  border-radius: ${(props) => components.button.borderRadius[props.size]};

  ${(props) =>
    props.fullWidth &&
    css`
      width: 100%;
    `}

  &:hover:not(:disabled),
  &:focus-visible:not(:disabled) {
    background-color: ${(props) =>
      props.hoverBgColor ||
      interaction.button[getVariantKey(props.variant)].hover.background};
    color: ${(props) =>
      props.hoverTextColor ||
      interaction.button[getVariantKey(props.variant)].hover.color};
    border-color: ${(props) =>
      props.hoverBorderColor ||
      interaction.button[getVariantKey(props.variant)].hover.border};
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const Button = ({ as, href, children, ...rest }) => {
  const component = as || (href ? "a" : "button");
  const isNativeButton = component === "button";

  return (
    <ButtonStyles
      as={component}
      href={href}
      draggable={isNativeButton ? undefined : false}
      {...rest}
    >
      {children}
    </ButtonStyles>
  );
};

Button.defaultProps = {
  size: "medium",
  variant: "solid",
};

Button.propTypes = {
  children: PropTypes.node.isRequired,
  as: PropTypes.elementType,
  bgColor: PropTypes.string,
  textColor: PropTypes.string,
  hoverBgColor: PropTypes.string,
  hoverTextColor: PropTypes.string,
  hoverBorderColor: PropTypes.string,
  variant: PropTypes.oneOf(["outlined", "solid", "ghost"]),
  size: PropTypes.oneOf(["small", "medium", "large"]),
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  fullWidth: PropTypes.bool,
  href: PropTypes.string,
};

export default Button;
